import React, { useEffect, useState, useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { format, addDays, isValid } from "date-fns";
import {
  Control,
  FieldErrors,
  useFormContext,
  UseFormWatch,
} from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar as FullCalendar, type CalendarEvent } from "@/components/ui/full-calendar/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { Clock, CheckCircle, Calendar, Users } from "lucide-react";
import { Event, EventAvailability } from "@/types/event";
import { EventStatus } from "@/types/enums";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";

interface EventSlotEvent extends CalendarEvent {
  is_draft?: boolean;
  session_type?: 'regular' | 'special';
  max_attendees?: number;
  event_name?: string;
  facility_name?: string;
  location?: string;
}

interface EventSlotsAndScheduleProps {
  control: Control<Event>;
  errors: FieldErrors<Event>;
  watch: UseFormWatch<Event>;
  open: boolean;
  action: 'add' | 'edit';
}

const EventSlotsAndSchedule = ({ control, watch, open, action }: EventSlotsAndScheduleProps) => {
  const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  const form = useFormContext<Event>();
  const { watch: formWatch } = form;

  // Watch form data reactively from previous steps
  const startDate = formWatch("start_date");
  const endDate = formWatch("end_date");
  const maxAttendees = formWatch("max_attendees");
  const facilityName = formWatch("facility_name");
  const eventName = formWatch("name");
  const location = formWatch("location");
  const availability = formWatch("availability");

  // State for current month view
  const [currentDate, setCurrentDate] = useState<Date>(() => {
    if (startDate && startDate !== "") {
      try {
        const date = new Date(startDate);
        if (!isNaN(date.getTime())) {
          return date;
        }
      } catch (error) {
        console.warn("Invalid start date:", startDate, error);
      }
    }
    return new Date();
  });

  const [selectedSlot, setSelectedSlot] = useState<EventSlotEvent | null>(null);
  const [isViewSlotOpen, setIsViewSlotOpen] = useState(false);

  // Generate slots based on availability from form data
  // Each day gets ONE slot at the specified time (start_time to end_time)
  const generateEventSlotsFromForm = useMemo(() => {
    console.log("Generating Event Slots with:", {
      startDate,
      endDate,
      availability: Object.keys(availability || {}),
      maxAttendees,
    });

    if (!startDate || !endDate || !availability || !maxAttendees) {
      console.log("Missing required data for Event Slots");
      return [];
    }

    const slots: EventSlotEvent[] = [];

    // Determine the date range for slot generation
    const rangeStart = new Date(startDate);
    const rangeEnd = new Date(endDate);

    console.log("Date range for slot generation:", {
      rangeStart: format(rangeStart, "yyyy-MM-dd EEEE"),
      rangeEnd: format(rangeEnd, "yyyy-MM-dd EEEE"),
    });

    // Generate ONE slot for each day in the range
    let currentDay = new Date(rangeStart);
    let dayIndex = 1;
    
    while (currentDay <= rangeEnd) {
      const dayName = format(currentDay, "EEEE");
      const dayKey = dayName.toLowerCase() as 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';
      const dayConfig = availability[dayKey];

      // Only create a slot if this day is available and has time configured
      if (dayConfig?.is_available &&
          dayConfig.time_slots.start_time &&
          dayConfig.time_slots.end_time) {

        const startTime = dayConfig.time_slots.start_time;
        const endTime = dayConfig.time_slots.end_time;

        // Parse times
        const [startHours, startMinutes] = startTime.split(":").map(Number);
        const [endHours, endMinutes] = endTime.split(":").map(Number);

        // Create the slot start datetime
        const slotStart = new Date(currentDay);
        slotStart.setHours(startHours, startMinutes, 0, 0);

        // Create the slot end datetime
        const slotEnd = new Date(currentDay);
        slotEnd.setHours(endHours, endMinutes, 0, 0);

        // Calculate duration in minutes
        const durationMinutes = Math.round((slotEnd.getTime() - slotStart.getTime()) / (1000 * 60));

        // Determine slot status based on date
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const slotDate = new Date(currentDay);
        slotDate.setHours(0, 0, 0, 0);
        
        let slotStatus: 'upcoming' | 'completed';
        let statusColor: string;
        if (slotDate < today) {
          slotStatus = 'completed';
          statusColor = 'gray';
        } else {
          slotStatus = 'upcoming';
          statusColor = 'blue';
        }

        // Determine tag based on action and status
        let tagLabel: string;
        let tagColor: string;
        
        if (action === 'add') {
          // Creating new event - always draft
          tagLabel = 'Draft Event';
          tagColor = 'blue';
        } else {
          // Editing existing event - current event
          tagLabel = 'Current Event';
          tagColor = 'orange';
        }

        // Add status tag
        const statusTag = {
          label: slotStatus === 'upcoming' ? 'Upcoming' : 'Completed',
          color: statusColor,
        };

        const dayLabel = `Day ${dayIndex}`;
        const timeLabel = `${format(slotStart, "HH:mm")} - ${format(slotEnd, "HH:mm")}`;
        const fullTitle = `${dayLabel} • ${timeLabel}`;

        slots.push({
          id: `${currentDay.getTime()}-day${dayIndex}`,
          title: fullTitle,
          start: slotStart,
          end: slotEnd,
          color: action === 'add' ? "blue" : "orange",
          tags: [
            {
              label: tagLabel,
              color: tagColor,
            },
            statusTag,
          ],
          user: {
            name: "Event Session",
          },
          description: `Event session Day ${dayIndex} from ${format(slotStart, "HH:mm")} to ${format(slotEnd, "HH:mm")} (${durationMinutes} min) - Max attendees: ${maxAttendees}`,
          location: location || "Event Location",
          event_name: eventName || "",
          facility_name: facilityName || "",
          is_draft: true,
          session_type: 'regular',
          max_attendees: maxAttendees,
        } as EventSlotEvent);

        dayIndex++;
      }

      // Move to next day
      currentDay = addDays(currentDay, 1);
    }

    console.log("Generated Event Slots:", slots.length);
    return slots;
  }, [startDate, endDate, availability, maxAttendees, facilityName, eventName, location, action]);

  // Navigation functions
  const handlePrevious = () => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev.getFullYear(), prev.getMonth() - 1, 1);
      return newDate;
    });
  };

  const handleNext = () => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev.getFullYear(), prev.getMonth() + 1, 1);
      return newDate;
    });
  };

  const handleViewSlot = (event: EventSlotEvent) => {
    setSelectedSlot(event);
    setIsViewSlotOpen(true);
  };

  const formatDate = (date: Date | string) => {
    const dateObj = typeof date === "string" ? new Date(date) : date;
    return dateObj.toLocaleString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });
  };

  // Check if we should show the calendar
  const shouldShowCalendar = startDate && endDate && availability && maxAttendees;

  const allEvents = generateEventSlotsFromForm;

  return (
    <div className="space-y-4" data-component="event-slots-and-schedule">
      <Card className="p-4">
        <p className="font-medium text-base">Event Schedule</p>
        <Separator className="h-[1px]" />

        {!shouldShowCalendar && (
          <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-md">
            <div className="flex items-center text-amber-800">
              <Clock className="h-5 w-5 mr-2" />
              <span>
                Please complete the previous steps to view event sessions
              </span>
            </div>
          </div>
        )}

        {shouldShowCalendar && (
          <>
            {/* Legend */}
            <div className="flex items-center space-x-4 mb-4 p-3 bg-gray-50 rounded-md flex-wrap">
              <div className="flex items-center space-x-2">
                <div className={`w-4 h-4 rounded ${action === 'add' ? 'bg-blue-500' : 'bg-orange-500'}`}></div>
                <span className="text-sm">{action === 'add' ? 'Draft Event' : 'Current Event'}</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-blue-500 rounded"></div>
                <span className="text-sm">Upcoming</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-gray-500 rounded"></div>
                <span className="text-sm">Completed</span>
              </div>
              <div className="text-sm text-gray-600">
                Max Attendees: {maxAttendees}
              </div>
              <div className="text-sm text-gray-600">
                Total Days: {allEvents.length}
              </div>
              {startDate && endDate && (
                <div className="text-sm text-gray-600">
                  Date Range: {format(new Date(startDate), "dd MMM yyyy")} - {format(new Date(endDate), "dd MMM yyyy")}
                </div>
              )}
            </div>

            {/* Calendar */}
            <div className="relative w-full h-[600px]">
              <FullCalendar
                events={allEvents}
                initialView="month"
                initialDate={currentDate}
                key={`calendar-${currentDate.getTime()}`}
                isCustomView={true}
                onCreateEvent={undefined} // Disable create event
                onViewEvent={handleViewSlot}
                onFilterEvents={undefined} // Disable filter
                onPrevious={handlePrevious}
                onNext={handleNext}
                disableNavigation={false}
              />
            </div>

            {/* Slot Details Dialog */}
            <Dialog open={isViewSlotOpen} onOpenChange={setIsViewSlotOpen}>
              <DialogContent className="sm:max-w-[480px]">
                <DialogHeader>
                  <DialogTitle className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                    Event Session Details
                  </DialogTitle>
                </DialogHeader>
                {selectedSlot && (
                  <div className="space-y-4">
                    <div className="grid gap-3">
                      <div className="flex items-center">
                        <span className="font-medium w-32">Event Name:</span>
                        <span className="ml-2">
                          {selectedSlot.event_name || "N/A"}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <span className="font-medium w-32">Facility:</span>
                        <span className="ml-2">
                          {selectedSlot.facility_name || "N/A"}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <span className="font-medium w-32">Session:</span>
                        <span className="ml-2">
                          {selectedSlot.title}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <span className="font-medium w-32">Date:</span>
                        <span className="ml-2">
                          {format(selectedSlot.start, "EEEE, MMMM dd, yyyy")}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <span className="font-medium w-32">Time:</span>
                        <span className="ml-2">
                          {format(selectedSlot.start, "HH:mm")} - {format(selectedSlot.end, "HH:mm")}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <span className="font-medium w-32">Duration:</span>
                        <span className="ml-2">
                          {(() => {
                            const startDate = new Date(selectedSlot.start);
                            const endDate = new Date(selectedSlot.end);
                            const minutes = isNaN(startDate.getTime()) || isNaN(endDate.getTime())
                              ? 0
                              : Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60));
                            const hours = Math.floor(minutes / 60);
                            const mins = minutes % 60;
                            return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
                          })()}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <span className="font-medium w-32">Max Attendees:</span>
                        <Badge variant="secondary" className="ml-2">
                          <Users className="h-3 w-3 mr-1" />
                          {selectedSlot.max_attendees}
                        </Badge>
                      </div>
                      <div className="flex items-center">
                        <span className="font-medium w-32">Location:</span>
                        <span className="ml-2">
                          {selectedSlot.location || "N/A"}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <span className="font-medium w-32">Type:</span>
                        <Badge variant="outline" className="ml-2">
                          {selectedSlot.session_type === 'regular' ? 'Regular Session' : 'Special Session'}
                        </Badge>
                      </div>
                    </div>
                  </div>
                )}
                <DialogFooter>
                  <DialogClose asChild>
                    <Button>Close</Button>
                  </DialogClose>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}
      </Card>
    </div>
  );
};

export default EventSlotsAndSchedule;
